import * as React from "react";
import { stateOptions } from "data";
import { ModalContext } from "app/app";
import { USDotNumber } from "./USDotNumber";
import { HasLossRuns } from "./LossRuns";
import { HasInsuredOperatedUnderDotNum } from "./HasInsuredOperatedUnderDotNum";
import { InsuredNames } from "./InsuredNames";
import { Address } from "./Address";
import { useWatch } from "react-hook-form";
import { InsuredContactInfo } from "./InsuredContactInfo";
import { BussinessOwnerInfo } from "./BussinessOwnerInfo";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../../components/ui/alert";
import { Button } from "../../../../components/ui/button";
import { AlertTriangle } from "lucide-react";
import { Fein } from "./Fein";
import { Revenue } from "./Revenue";
import { Miles } from "./Miles";
import { MotorCarrierNumber } from "./MotorCarrierNumber";

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const Insured = React.memo(function Step2({ methods, agencyData }) {
  const { register, control, setValue, trigger, formState: { errors } } = methods;
  const { setModal } = React.useContext(ModalContext);

  const mailingAddress = useWatch({
    control,
    name: "step2.mailingAddress",
  });
  const garagingAddress = useWatch({
    control,
    name: "step2.garagingAddress",
  });

  const garagingSameAsMailing = useWatch({
    control,
    name: "step2.garagingSameAsMailing",
  });

  const step1Carrier = useWatch({ control, name: "step1.carrier" });
  const step2Carrier = useWatch({ control, name: "step2.carrier" });

  React.useEffect(() => {
    register("step2.carrier");
  }, [register]);

  React.useEffect(() => {
    const list = Array.isArray(step1Carrier)
      ? step1Carrier
      : step1Carrier ? [step1Carrier] : [];
    const normalized = list.map((s) => (s ?? ""));

    if (!arraysEqual(normalized, step2Carrier ?? [])) {
      setValue("step2.carrier", normalized, { shouldDirty: false, shouldValidate: true });
    }
  }, [step1Carrier, step2Carrier, setValue]);

  const agencyQuatableStates = React.useMemo(() => {
    if (agencyData.loading || !agencyData.data?.users) return [];
    if (agencyData.data?.states) {
      return stateOptions.filter((stateOption) =>
        agencyData.data.states.includes(stateOption.value)
      );
    }
  }, [agencyData.loading, agencyData.data]);

  const canUseGaragingSameAsMailing = React.useMemo(() => {
    if (!mailingAddress?.state || !agencyQuatableStates?.length) return false;
    return agencyQuatableStates.some(
      (stateOption) => stateOption.value === mailingAddress.state
    );
  }, [mailingAddress?.state, agencyQuatableStates]);

  React.useEffect(() => {
    if (!canUseGaragingSameAsMailing && garagingSameAsMailing) {
      setValue("step2.garagingSameAsMailing", false, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [canUseGaragingSameAsMailing, garagingSameAsMailing, setValue]);

  React.useEffect(() => {
    if (!garagingSameAsMailing) return;
    if (!canUseGaragingSameAsMailing) return;
    if (!mailingAddress) return;

    setValue("step2.garagingAddress", mailingAddress, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [
    garagingSameAsMailing,
    canUseGaragingSameAsMailing,
    mailingAddress,
    setValue,
  ]);

  const invalidDocumentsList = React.useMemo(() => {
    const data = agencyData?.data;
    const errors = [];

    if (data?.documents_need_update && data?.documents_need_update?.length > 0) {
      data?.documents_need_update?.forEach((doc) => {
        if (doc.includes("license_")) {
          const state = doc.split("_")[1];

          if (
            (garagingAddress?.state && garagingAddress?.state === state) ||
            (mailingAddress?.state && mailingAddress?.state === state)
          ) {
            setValue(
              garagingAddress?.state === state
                ? "step2.garagingAddress.state"
                : "step2.mailingAddress.state",
              undefined,
              { shouldDirty: true }
            );
            const error = {
              title: `Your ${state} License Has Expired!`,
              description: `Our records indicate that your agency’s ${state} License has expired or we do not have a valid ${state} License on file.`,
              description2: `In order to continue with this quote, please use the link below to update your agency’s ${state} License.`,
              documentLabel: `${state} License`,
              documentSlug: `licenses`,
            };
            setModal({
              name: "expired_document",
              data: error,
              action: async () => {
                setModal("");
              },
            });
          }
        }
      });

      if (data?.documents_need_update?.includes("eo")) {
        errors.push({
          title: `Your E&O Policy Has Expired!`,
          description:
            "Our records indicate that your agency’s E&O policy has expired or we do not have a valid E&O policy on file.",
          description2:
            "In order to continue with this quote, please use the link below to update your agency’s E&O policy.",
          documentLabel: `E&O Policy`,
          documentSlug: `eo`,
        });
      }
    }

    return errors;
  }, [agencyData.loading, agencyData.data, mailingAddress, garagingAddress]);

  if (invalidDocumentsList?.length > 0) {
    return (
      <div className="flex flex-col justify-start items-center h-full">
        {invalidDocumentsList.map((doc, index) => (
          <div
            className="flex justify-center items-center h-full"
            key={`invalid-document-${index}`}
          >
            <Alert variant="destructive" className="max-w-md border-0">
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle>{doc.title}</AlertTitle>
              <AlertDescription>
                {doc.description}&nbsp;{doc.description2}
                <div>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() =>
                      window.open(`/app/onboarding?update=${doc.documentSlug}`)
                    }
                  >
                    Change
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Insured */}
      <div className="rounded-lg p-0 space-y-4">
        <h3 className="text-lg font-semibold">Insured</h3>

        <MotorCarrierNumber control={control} register={register} />

        <USDotNumber
          control={control}
          trigger={trigger}
          errors={errors}
          register={register}
          setValue={setValue}
        />

        <HasLossRuns control={control} />

        <HasInsuredOperatedUnderDotNum control={control} setValue={setValue} />

        {/* Names */}
        <InsuredNames register={register} />
        {step2Carrier?.includes('Accelerant') && <Fein control={control} register={register} />}
        <Revenue control={control} register={register} setValue={setValue} carriers={step2Carrier} />
        <Miles control={control} register={register} setValue={setValue} carriers={step2Carrier} />
      </div>

      <Address
        register={register}
        control={control}
        states={stateOptions}
        title="Mailing address"
        formKey={"step2.mailingAddress"}
        stepId={"step2"}
        sameAs={
          canUseGaragingSameAsMailing
            ? {
                id: "garaging-same-as-mailing",
                label: "Business garaging address is same as mailing address",
                formKey: "step2.garagingSameAsMailing",
              }
            : undefined
        }
        disableFields={false}
      />

      <Address
        register={register}
        control={control}
        states={agencyQuatableStates}
        title="Business garaging address"
        formKey={"step2.garagingAddress"}
        stepId={"step2"}
        disableFields={false}
      />

      <InsuredContactInfo register={register} setValue={setValue} />

      <BussinessOwnerInfo register={register} control={control} />
    </div>
  );
});
